// v2 monkey — a proper little primate on all fours: slim limbs ending in dark
// hands and feet, a furred body with a pale belly, a round head whose face is
// framed by a contrasting brow band, perky ears — and the signature long tail
// carried up over the back in a three-segment S-curl. Two forms: a grey vervet
// (dark face, pale brow) and a brown capuchin (pale face, dark brow). Ported 1:1
// from age-of-mcp's v2/monkey.ts, using the library's shared `safari`
// (eyes, tube) helpers.
import * as THREE from "three";
import { defineModel, safari, type Part } from "../../lib";

const FORMS = [
  { fur: "#8a8278", limb: "#736b60", belly: "#aaa090", face: "#2e2a24",
    brow: "#d8d2c2", hand: "#3a342c", eye: "#caa468" },
  { fur: "#6e4f32", limb: "#523a24", belly: "#caa87e", face: "#dcc09a",
    brow: "#3f2d1c", hand: "#3a2a1a", eye: "#1a1410" },
];

function monkeyParts(v: number): Part[] {
  const c = FORMS[v % FORMS.length]!;
  const parts: Part[] = [
    // furred body with a pale belly
    { geometry: new THREE.IcosahedronGeometry(0.0335, 0), color: c.fur,
      position: [0, 0.058, 0], scale: [1.3, 1.05, 0.92] },
    { geometry: new THREE.IcosahedronGeometry(0.023, 0), color: c.belly,
      position: [0.01, 0.05, 0], scale: [1.0, 0.85, 0.8] },
    // round head: face framed by the brow band, perky ears, tiny nose
    { geometry: new THREE.IcosahedronGeometry(0.0205, 0), color: c.fur,
      position: [0.036, 0.097, 0] },
    { geometry: new THREE.IcosahedronGeometry(0.0135, 0), color: c.face,
      position: [0.0525, 0.0945, 0], scale: [0.62, 1.05, 0.95] },
    { geometry: new THREE.IcosahedronGeometry(0.013, 0), color: c.brow,
      position: [0.046, 0.108, 0], scale: [0.85, 0.5, 1.05] },
    { geometry: new THREE.IcosahedronGeometry(0.0065, 0), color: c.limb,
      position: [0.027, 0.107, 0.0195] },
    { geometry: new THREE.IcosahedronGeometry(0.0065, 0), color: c.limb,
      position: [0.027, 0.107, -0.0195] },
    { geometry: new THREE.IcosahedronGeometry(0.004, 0), color: c.hand,
      position: [0.0605, 0.0925, 0] },
    // the long tail, carried up over the back in an S-curl
    safari.tube([-0.038, 0.062, 0], [-0.062, 0.088, 0], 0.0046, 0.004, c.fur),
    safari.tube([-0.062, 0.088, 0], [-0.072, 0.116, 0.003], 0.004, 0.0034, c.fur),
    safari.tube([-0.072, 0.116, 0.003], [-0.062, 0.138, 0.006], 0.0034, 0.0026, c.fur),
  ];
  // slim limbs ending in dark hands / feet
  for (const s of [1, -1] as const) {
    parts.push(
      safari.tube([0.026, 0.058, s * 0.0235], [0.034, 0.004, s * 0.0255],
        0.0055, 0.0042, c.limb),
      { geometry: new THREE.IcosahedronGeometry(0.0052, 0), color: c.hand,
        position: [0.035, 0.005, s * 0.0255] },
      safari.tube([-0.028, 0.056, s * 0.024], [-0.034, 0.004, s * 0.026],
        0.0058, 0.0044, c.limb),
      { geometry: new THREE.IcosahedronGeometry(0.0052, 0), color: c.hand,
        position: [-0.035, 0.005, s * 0.026] },
    );
  }
  parts.push(...safari.eyes(0.0555, 0.099, 0.0075, 0.0028, c.eye));
  return parts;
}

export const monkeyV2 = defineModel({
  id: "monkey-v2",
  scale: 1.98, // real-world size correction (see Model.scale)
  batchId: "fable",
  name: "Monkey (v2)",
  tags: ["creature","monkey","low-poly","age-of-mcp","fable"],
  summary: "Low-poly monkey.",
  description: "Ported from the age-of-mcp gallery, v2 style.",
  author: "age-of-mcp",
  footprint: 0.08,
  variants: [
    { id: "variant-0", label: "Variant 1", parts: () => monkeyParts(0) },
    { id: "variant-1", label: "Variant 2", parts: () => monkeyParts(1) },
  ],
  animations: [],
});
